/**
 * InsightCard Component
 * Reusable card for displaying individual insights (decisions, progress, questions, risks).
 */

import React from 'react';
import {
  CheckCircle2,
  Circle,
  AlertCircle,
  Clock,
  FileText,
  ArrowUpCircle,
  ArrowRightCircle,
  ArrowDownCircle,
} from 'lucide-react';

interface InsightCardProps {
  type: 'decision' | 'progress' | 'question' | 'risk';
  title: string;
  description?: string;
  source: string;
  status?: string;
  priority?: string;
  severity?: string;
  impact?: string;
  owner?: string;
  mitigation?: string;
}

const statusIcons: Record<string, React.ReactNode> = {
  completed: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  'in-progress': <Clock className="w-4 h-4 text-blue-600" />,
  blocked: <AlertCircle className="w-4 h-4 text-red-600" />,
  planned: <Circle className="w-4 h-4 text-slate-400" />,
  proposed: <Circle className="w-4 h-4 text-amber-500" />,
  approved: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  implemented: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
};

const priorityColors: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

const impactIcons: Record<string, React.ReactNode> = {
  high: <ArrowUpCircle className="w-3.5 h-3.5 text-red-600" />,
  medium: <ArrowRightCircle className="w-3.5 h-3.5 text-amber-600" />,
  low: <ArrowDownCircle className="w-3.5 h-3.5 text-slate-500" />,
};

export const InsightCard: React.FC<InsightCardProps> = ({
  type,
  title,
  description,
  source,
  status,
  priority,
  severity,
  impact,
  owner,
  mitigation,
}) => {
  const statusIcon = status ? statusIcons[status] : null;
  const priorityClass = priority ? priorityColors[priority] : '';
  const severityClass = severity ? severityColors[severity] : '';

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        {/* Status/Priority Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {type === 'risk' && severity && (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${severityClass}`}>
              <AlertCircle className="w-4 h-4" />
            </div>
          )}
          {type === 'question' && priority && (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${priorityClass}`}>
              <AlertCircle className="w-4 h-4" />
            </div>
          )}
          {type === 'progress' && statusIcon && (
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
              {statusIcon}
            </div>
          )}
          {type === 'decision' && (
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              {statusIcon || <FileText className="w-4 h-4 text-purple-600" />}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-slate-900 leading-snug">
            {title}
          </h4>

          {description && (
            <p className="mt-1 text-xs text-slate-600 line-clamp-2">
              {description}
            </p>
          )}

          {mitigation && (
            <p className="mt-1.5 text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1">
              <span className="font-medium">Mitigation:</span> {mitigation}
            </p>
          )}

          {/* Meta row */}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {/* Source */}
            <span className="text-slate-500 flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {source}
            </span>

            {/* Owner */}
            {owner && (
              <span className="text-slate-500">
                &middot; {owner}
              </span>
            )}

            {/* Impact badge */}
            {impact && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                {impactIcons[impact]}
                {impact}
              </span>
            )}

            {/* Status badge */}
            {status && type !== 'progress' && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">
                {status}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightCard;
